// SPDX-License-Identifier: GPL-3.0-or-later
// The "Featured turns" dialog of the game page. It owns the server answers (me, queue), the notes,
// the buttons and the turn session. The pure view model is in featured-view.ts; the stream rules
// are in featured-session.ts. A failure here never blocks free play.
// "Forget this device" deletes the paid turns of a wallet with no Twitch sign-in. It asks first,
// and the question names the loss (decision: a credit is never lost without a confirmation).

import type { MeResponse, QueueSnapshot } from "../src/shared/contracts.ts";
import {
  type ApiResult,
  createFeaturedApi,
  type FeaturedApi,
  type FetchLike,
  isClaimCodeShape,
  normalizeClaimCode,
} from "./api.ts";
import { asList, fillList, slot } from "./dom.ts";
import {
  createTurnSession,
  type SessionDeps,
  type StreamPhase,
  type TurnSession,
} from "./featured-session.ts";
import { featuredSocketUrl, type SocketLike } from "./featured-stream.ts";
import { buildPanelView, forgetWarning, type PanelView, signOutStep } from "./featured-view.ts";
import type { LiveFeed } from "./hud.ts";
import { endReasonText } from "./screens.ts";

/** How often the page asks the server about the waiting list while it matters. */
export const POLL_MS = 5000;

/** What the page gives the panel. */
export interface FeaturedPanelDeps {
  readonly doc: Document;
  readonly win: Window & typeof globalThis;
  readonly dialog: HTMLDialogElement;
  readonly feed: LiveFeed;
  /** True when no game runs. A featured turn starts only then. */
  readonly isGameIdle: () => boolean;
  /** Open the dialog as a modal. */
  readonly showDialog: () => void;
  /**
   * The Worker accepted the ticket. `resume` is true for a reconnect of a running game. `name` is
   * the donor's display name, or missing when the wallet has none.
   */
  readonly onTurnReady: (resume: boolean, name?: string) => void;
  /** The Worker ended the turn. `note` is the text for the game over dialog. */
  readonly onTurnEnd: (note: string) => void;
}

/** The panel. */
export interface FeaturedPanel {
  /** The turn session. The page passes it the engine events and the frames. */
  readonly session: TurnSession;
  /** Draw the dialog and ask the server once. The page calls it on the first screen. */
  start(): void;
  /** Show the dialog. */
  open(): void;
  /** Show the dialog on the claim form: the code field gets the focus when the form shows. */
  openForClaim(): void;
}

/** The nodes of the dialog. */
interface Nodes {
  readonly status: HTMLElement;
  readonly credit: HTMLElement;
  readonly expiry: HTMLElement;
  readonly hint: HTMLElement;
  readonly note: HTMLElement;
  readonly queueTitle: HTMLElement;
  readonly queue: HTMLElement;
  readonly signin: HTMLAnchorElement;
  readonly signout: HTMLButtonElement;
  readonly keep: HTMLButtonElement;
  readonly claimForm: HTMLFormElement;
  readonly claimInput: HTMLInputElement;
  readonly claimName: HTMLInputElement;
  readonly join: HTMLButtonElement;
  readonly leave: HTMLButtonElement;
  readonly start: HTMLButtonElement;
  readonly reconnect: HTMLButtonElement;
  readonly toolButton: HTMLButtonElement;
}

/** The state of the panel. */
interface State {
  apiState: "unknown" | "offline" | "ready";
  me: MeResponse | null;
  queue: QueueSnapshot | null;
  stream: StreamPhase;
  note: string;
  readyAnnounced: boolean;
  /** True when the page opened on /claim. The first answer shows the form; then it gets the focus. */
  claimFocusPending: boolean;
  /** The time of the "Forget this device" question, or null when nobody was asked. */
  forgetAskedAt: number | null;
}

interface Core {
  readonly deps: FeaturedPanelDeps;
  readonly api: FeaturedApi;
  readonly nodes: Nodes;
  readonly state: State;
}

interface Ctx extends Core {
  readonly session: TurnSession;
}

function findNodes(doc: Document): Nodes {
  const q = <T extends HTMLElement = HTMLElement>(name: string): T => slot<T>(doc, name);
  return {
    status: q("feat-status"),
    credit: q("feat-credit"),
    expiry: q("feat-expiry"),
    hint: q("feat-hint"),
    note: q("feat-note"),
    queueTitle: q("feat-queue-title"),
    queue: q("feat-queue"),
    signin: q<HTMLAnchorElement>("feat-signin"),
    signout: q<HTMLButtonElement>("feat-signout"),
    keep: q<HTMLButtonElement>("feat-keep"),
    claimForm: q<HTMLFormElement>("feat-claim-form"),
    claimInput: q<HTMLInputElement>("feat-claim-input"),
    claimName: q<HTMLInputElement>("feat-claim-name"),
    join: q<HTMLButtonElement>("feat-join"),
    leave: q<HTMLButtonElement>("feat-leave"),
    start: q<HTMLButtonElement>("feat-start"),
    reconnect: q<HTMLButtonElement>("feat-reconnect"),
    toolButton: q<HTMLButtonElement>("btn-featured"),
  };
}

function applyPanel(core: Core, v: PanelView): void {
  const { nodes } = core;
  nodes.status.textContent = v.status;
  nodes.credit.textContent = v.credit;
  nodes.expiry.textContent = v.expiry;
  nodes.hint.textContent = v.hint;
  nodes.note.textContent = core.state.note;
  nodes.signin.hidden = !v.canSignIn;
  nodes.signout.hidden = !v.canSignOut;
  nodes.signout.textContent = v.signOutLabel;
  nodes.keep.hidden = !v.canKeep;
  nodes.claimForm.hidden = !v.canClaim;
  nodes.join.hidden = !v.canJoin;
  nodes.leave.hidden = !v.canLeave;
  nodes.start.hidden = !v.canStart;
  nodes.reconnect.hidden = !v.canReconnect;
  nodes.queueTitle.textContent = v.queueTitle;
  fillList(asList(nodes.queue), v.queueLines);
}

function renderPanel(core: Core): void {
  const s = core.state;
  applyPanel(
    core,
    buildPanelView({
      api: s.apiState,
      me: s.me,
      queue: s.queue,
      gameIdle: core.deps.isGameIdle(),
      stream: s.stream,
      confirmForget: s.forgetAskedAt !== null,
    }),
  );
  const ready = s.me?.entry?.state === "ready";
  core.nodes.toolButton.textContent = ready ? "Featured turns: ready" : "Featured turns";
}

/** Tell the player once when a turn becomes ready. */
function announceReady(core: Core): void {
  const s = core.state;
  const isReady = s.me?.entry?.state === "ready";
  if (isReady && !s.readyAnnounced) {
    core.deps.feed.push("Your featured turn is ready. Open Featured turns to start it.", "high");
  }
  s.readyAnnounced = isReady;
}

/** The claim form is hidden until the first answer. Then the code field gets the focus. */
function focusClaimInput(core: Core): void {
  const s = core.state;
  if (!s.claimFocusPending || core.nodes.claimForm.hidden) return;
  s.claimFocusPending = false;
  core.nodes.claimInput.focus();
}

async function refresh(core: Core): Promise<void> {
  const [meResult, queueResult] = await Promise.all([core.api.me(), core.api.queue()]);
  const s = core.state;
  // No answer, or an error answer (for example 503 before the secrets are set): offline.
  s.apiState = meResult.ok ? "ready" : "offline";
  s.me = meResult.ok ? meResult.value : null;
  s.queue = queueResult.ok ? queueResult.value : null;
  // A device that holds nothing needs no question any more.
  if (forgetWarning(s.me) === null) s.forgetAskedAt = null;
  announceReady(core);
  renderPanel(core);
  focusClaimInput(core);
}

function openPanel(core: Core): void {
  core.state.note = "";
  core.state.forgetAskedAt = null;
  renderPanel(core);
  core.deps.showDialog();
  void refresh(core);
}

async function startTurn(ctx: Ctx, resume: boolean): Promise<void> {
  const s = ctx.state;
  if (!resume && !ctx.deps.isGameIdle()) {
    s.note = "Finish your free game first.";
    renderPanel(ctx);
    return;
  }
  // A second click while a socket connects must not open a second stream.
  if (ctx.session.blocksFreeStart()) return;
  const result = await ctx.api.startTurn();
  if (!result.ok) {
    s.note = result.message;
    renderPanel(ctx);
    return;
  }
  ctx.session.connect(result.value.ticket, resume);
}

/** Run a call on the waiting list, then show its note and ask the server again. */
function queueAction(core: Core, call: () => Promise<ApiResult<unknown>>, okNote: string): void {
  void call().then((r) => {
    core.state.note = r.ok ? okNote : r.message;
    return refresh(core);
  });
}

function onSignOut(core: Core): void {
  const s = core.state;
  const step = signOutStep(s.me, s.forgetAskedAt, core.deps.win.performance.now());
  if (step === "wait") return;
  if (step === "ask") {
    s.forgetAskedAt = core.deps.win.performance.now();
    s.note = forgetWarning(s.me) ?? "";
    renderPanel(core);
    return;
  }
  s.forgetAskedAt = null;
  void core.api.logout().then(() => {
    s.note = "";
    return refresh(core);
  });
}

/** The player keeps the device: nothing is deleted. */
function onKeep(core: Core): void {
  core.state.forgetAskedAt = null;
  core.state.note = "Nothing was deleted.";
  renderPanel(core);
}

function claimNote(r: ApiResult<{ readonly turns_added: number }>): string {
  if (!r.ok) return r.message;
  const n = r.value.turns_added;
  return `Added ${n} featured ${n === 1 ? "turn" : "turns"}.`;
}

function onClaimSubmit(core: Core, e: Event): void {
  e.preventDefault();
  const { claimInput, claimName } = core.nodes;
  const code = normalizeClaimCode(claimInput.value);
  if (!isClaimCodeShape(code)) {
    core.state.note = "That code does not look right. A code looks like PB-ABCD-1234.";
    renderPanel(core);
    return;
  }
  void core.api.claim(code, claimName.value).then((r) => {
    if (r.ok) claimInput.value = "";
    core.state.note = claimNote(r);
    return refresh(core);
  });
}

function bindButtons(ctx: Ctx): void {
  const { nodes, state } = ctx;
  nodes.start.addEventListener("click", () => void startTurn(ctx, false));
  nodes.reconnect.addEventListener("click", () => void startTurn(ctx, state.stream === "lost"));
  nodes.join.addEventListener("click", () =>
    queueAction(ctx, () => ctx.api.join(), "You joined the waiting list."),
  );
  nodes.leave.addEventListener("click", () =>
    queueAction(ctx, () => ctx.api.leave(), "You left the waiting list. Your turn is kept."),
  );
  nodes.signout.addEventListener("click", () => onSignOut(ctx));
  nodes.keep.addEventListener("click", () => onKeep(ctx));
  nodes.claimForm.addEventListener("submit", (e) => onClaimSubmit(ctx, e));
  // A closed dialog drops a question that nobody answered.
  ctx.deps.dialog.addEventListener("close", () => {
    state.forgetAskedAt = null;
  });
}

/** Open the socket. A browser that cannot open it gives null, and the note says so. */
function openSocket(core: Core): SocketLike | null {
  const { win } = core.deps;
  try {
    return new win.WebSocket(featuredSocketUrl(win.location)) as unknown as SocketLike;
  } catch {
    core.state.note = "The stream connection could not open. Free play is not affected.";
    return null;
  }
}

/** The donor's display name of the turn that starts, or missing when the wallet has none. */
function donorName(core: Core): string | undefined {
  return core.state.me?.display_name ?? undefined;
}

/** The session owns the stream rules (seq base, final frame after a lost stream, connecting state). */
function sessionDeps(core: Core): SessionDeps {
  const { deps, state } = core;
  return {
    openSocket: () => openSocket(core),
    now: () => Date.now(),
    onPhase: (next) => {
      state.stream = next;
      renderPanel(core);
    },
    onReady: (resume) => deps.onTurnReady(resume, donorName(core)),
    onEnd: (msg) => {
      state.note = endReasonText(msg.reason);
      deps.onTurnEnd(state.note);
      void refresh(core);
    },
    onError: (msg) => {
      state.note = msg.message;
      renderPanel(core);
    },
  };
}

/** True while the player waits for a turn that is not live. */
function entryWaits(state: State): boolean {
  return state.me?.entry != null && state.me.entry.state !== "live";
}

/** Make the panel, bind its buttons and start the poll. */
export function createFeaturedPanel(deps: FeaturedPanelDeps): FeaturedPanel {
  const { doc, win } = deps;
  const core: Core = {
    deps,
    api: createFeaturedApi(((input, init) => win.fetch(input, init)) as FetchLike),
    nodes: findNodes(doc),
    state: {
      apiState: "unknown",
      me: null,
      queue: null,
      stream: "none",
      note: "",
      readyAnnounced: false,
      claimFocusPending: false,
      forgetAskedAt: null,
    },
  };
  const ctx: Ctx = { ...core, session: createTurnSession(sessionDeps(core)) };
  bindButtons(ctx);
  win.setInterval(() => {
    if (!doc.hidden && (deps.dialog.open || entryWaits(core.state))) void refresh(core);
  }, POLL_MS);
  return {
    session: ctx.session,
    start: () => {
      renderPanel(core);
      void refresh(core);
    },
    open: () => openPanel(core),
    openForClaim: () => {
      core.state.claimFocusPending = true;
      openPanel(core);
    },
  };
}
